/**
 * USt-IdNr-Prüfung nach VIES-Format.
 *
 * Geprüft wird das Format aller EU-Länder (inklusive XI für Nordirland); zusätzlich
 * wird für DE und AT die Prüfziffer verifiziert. Eine Online-Abfrage gegen VIES ist
 * bewusst nicht Teil dieser Funktion — sie muss ohne Netzwerkzugriff nutzbar bleiben.
 */
/** Länderpräfixe mit ihrem VIES-Format (ohne Länderkürzel). */
const VAT_ID_PATTERNS = {
    AT: /^U\d{8}$/,
    BE: /^[01]\d{9}$/,
    BG: /^\d{9,10}$/,
    CY: /^\d{8}[A-Z]$/,
    CZ: /^\d{8,10}$/,
    DE: /^\d{9}$/,
    DK: /^\d{8}$/,
    EE: /^\d{9}$/,
    EL: /^\d{9}$/,
    ES: /^[A-Z0-9]\d{7}[A-Z0-9]$/,
    FI: /^\d{8}$/,
    FR: /^[A-Z0-9]{2}\d{9}$/,
    HR: /^\d{11}$/,
    HU: /^\d{8}$/,
    IE: /^(\d{7}[A-W]|\d[A-Z*+]\d{5}[A-W]|\d{7}[A-W][AH])$/,
    IT: /^\d{11}$/,
    LT: /^(\d{9}|\d{12})$/,
    LU: /^\d{8}$/,
    LV: /^\d{11}$/,
    MT: /^\d{8}$/,
    NL: /^\d{9}B\d{2}$/,
    PL: /^\d{10}$/,
    PT: /^\d{9}$/,
    RO: /^\d{2,10}$/,
    SE: /^\d{12}$/,
    SI: /^\d{8}$/,
    SK: /^\d{10}$/,
    XI: /^(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/,
};
export const VAT_ID_COUNTRY_CODES = Object.keys(VAT_ID_PATTERNS);
/** ISO-Ländercodes der EU; `EL` wird in USt-IdNr für Griechenland (GR) verwendet. */
export const EU_COUNTRY_CODES = [
    'AT',
    'BE',
    'BG',
    'CY',
    'CZ',
    'DE',
    'DK',
    'EE',
    'ES',
    'FI',
    'FR',
    'GR',
    'HR',
    'HU',
    'IE',
    'IT',
    'LT',
    'LU',
    'LV',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SE',
    'SI',
    'SK',
];
/** Entfernt Leerzeichen, Punkte und Bindestriche und schreibt groß. */
export function normalizeVatId(value) {
    return value.replace(/[\s.\-/]/g, '').toUpperCase();
}
function digitsOf(value) {
    return [...value].map((character) => Number.parseInt(character, 10));
}
/** Prüfziffernverfahren der deutschen USt-IdNr (modifiziertes Modulo-11). */
function hasValidGermanChecksum(digits) {
    const values = digitsOf(digits);
    const checkDigit = values[8];
    if (checkDigit === undefined) {
        return false;
    }
    let product = 10;
    for (let index = 0; index < 8; index += 1) {
        const digit = values[index];
        if (digit === undefined) {
            return false;
        }
        const sum = (digit + product) % 10;
        const normalizedSum = sum === 0 ? 10 : sum;
        product = (normalizedSum * 2) % 11;
    }
    const expected = (11 - product) % 10;
    return expected === checkDigit;
}
/** Prüfziffernverfahren der österreichischen UID (ATU + 8 Stellen). */
function hasValidAustrianChecksum(body) {
    const values = digitsOf(body.slice(1));
    const checkDigit = values[7];
    if (checkDigit === undefined) {
        return false;
    }
    let total = 0;
    for (let index = 0; index < 7; index += 1) {
        const digit = values[index];
        if (digit === undefined) {
            return false;
        }
        if (index % 2 === 0) {
            total += digit;
            continue;
        }
        const doubled = digit * 2;
        total += Math.floor(doubled / 10) + (doubled % 10);
    }
    return (96 - total) % 10 === checkDigit;
}
function verifyChecksum(countryCode, body) {
    switch (countryCode) {
        case 'DE':
            return hasValidGermanChecksum(body);
        case 'AT':
            return hasValidAustrianChecksum(body);
        default:
            return undefined;
    }
}
export function validateVatId(input) {
    const normalized = normalizeVatId(input);
    if (normalized.length === 0) {
        return { valid: false, reason: 'EMPTY' };
    }
    const countryCode = normalized.slice(0, 2);
    const body = normalized.slice(2);
    const pattern = VAT_ID_PATTERNS[countryCode];
    if (pattern === undefined) {
        return { valid: false, reason: 'COUNTRY_UNKNOWN' };
    }
    if (!pattern.test(body)) {
        return { valid: false, reason: 'FORMAT_INVALID' };
    }
    const checksum = verifyChecksum(countryCode, body);
    if (checksum === false) {
        return { valid: false, reason: 'CHECKSUM_INVALID' };
    }
    return {
        valid: true,
        normalized,
        countryCode,
        checksumVerified: checksum === true,
    };
}
export function isValidVatId(input) {
    return validateVatId(input).valid;
}
//# sourceMappingURL=vat-id.js.map